import { useMemo, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import AutomaticPaymentMethods from '../../GestionHotel/MoteurReservation/components/AutomaticPaymentMethods'
import { labelClass } from '../shared'
import { transferStyles as styles } from '../shared/transferStyles'
import { isAffilieUser, isPartnerOrAffilieUser } from './transferBillingIds'

/** Référence stable quand aucun paiement encore choisi (évite boucle useEffect sur `value`). */
const EMPTY_PAYMENT_METHODS = Object.freeze({})

function paymentMethodsSignature(v) {
  if (!v || typeof v !== 'object') return '{}'
  try {
    return JSON.stringify(v)
  } catch {
    return '{}'
  }
}

/**
 * Paiement B2B (débiteur / dépôt) — même logique que le moteur réservation hôtel.
 * Affilié: UI masquée, méthodes auto du partenaire parent toujours appliquées.
 */
export default function TransferMoralePaymentSection({
  draft,
  setField,
  partnerIdOverride,
  hideUi = false,
  appearance = 'default',
}) {
  const { t } = useTranslation()
  const hidePaymentUi = hideUi || isAffilieUser()
  const lockPaymentAmount = isPartnerOrAffilieUser()

  const total = useMemo(() => {
    const n = draft.amount !== '' && draft.amount != null ? Number(draft.amount) : 0
    return Number.isFinite(n) ? n : 0
  }, [draft.amount])

  const deductNotice = useMemo(() => {
    if (!lockPaymentAmount || total <= 0) return null
    return t('transferts.reservations.payment.deductNotice', {
      amount: Number(total).toFixed(2),
      currency: 'TND',
    })
  }, [lockPaymentAmount, total, t])

  const partnerId = useMemo(() => {
    if (partnerIdOverride != null && partnerIdOverride !== '') {
      const n = Number(partnerIdOverride)
      return Number.isFinite(n) && n > 0 ? n : null
    }
    if (draft.billing_partner_id != null && draft.billing_partner_id !== '') {
      const n = Number(draft.billing_partner_id)
      return Number.isFinite(n) && n > 0 ? n : null
    }
    if (draft.billing_client_id != null && draft.billing_client_id !== '') {
      const n = Number(draft.billing_client_id)
      return Number.isFinite(n) && n > 0 ? n : null
    }
    return null
  }, [partnerIdOverride, draft.billing_partner_id, draft.billing_client_id])

  const paymentClient = useMemo(() => {
    if (partnerId == null) return null
    return { id: partnerId, segment: 'B2B' }
  }, [partnerId])

  const bookingDate = useMemo(() => {
    if (!draft.date_pickup || !dayjs(draft.date_pickup).isValid()) return null
    return dayjs(draft.date_pickup).format('YYYY-MM-DD')
  }, [draft.date_pickup])

  const valueForPicker = useMemo(() => {
    const pm = draft.payment_methods
    if (pm && typeof pm === 'object' && Object.keys(pm).length > 0) return pm
    return EMPTY_PAYMENT_METHODS
  }, [draft.payment_methods])

  const lastEmittedSigRef = useRef(paymentMethodsSignature(valueForPicker))
  const fetchKeyRef = useRef('')

  useEffect(() => {
    const key = `${partnerId ?? ''}|${total}|${bookingDate ?? ''}`
    if (fetchKeyRef.current !== key) {
      fetchKeyRef.current = key
      lastEmittedSigRef.current = '{}'
    }
  }, [partnerId, total, bookingDate])

  const handlePaymentChange = useCallback(
    (v) => {
      const next = v && typeof v === 'object' && !Array.isArray(v) ? v : {}
      const sig = paymentMethodsSignature(next)
      if (sig === lastEmittedSigRef.current) return
      lastEmittedSigRef.current = sig
      setField('payment_methods', Object.keys(next).length ? next : {})
      if (hidePaymentUi) setField('payment_bundle', null)
    },
    [setField, hidePaymentUi],
  )

  const tamarra = appearance === 'tamarra'

  if (!paymentClient) {
    if (hidePaymentUi) return null
    return (
      <div className={tamarra ? 'tamarra-banner' : 'rounded-[var(--radius-md)] border border-amber-100 bg-amber-50 px-3 py-2 text-sm text-amber-900'} role="status">
        {t('transferts.reservations.payment.selectMorale')}
      </div>
    )
  }

  if (total <= 0) {
    if (hidePaymentUi) return null
    return (
      <div className={tamarra ? 'tamarra-bk-rate' : 'rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-sm text-[var(--muted-foreground)]'}>
        {t('transferts.reservations.payment.enterAmount')}
      </div>
    )
  }

  return (
    <div className={hidePaymentUi ? 'hidden' : tamarra ? undefined : 'space-y-2'} aria-hidden={hidePaymentUi}>
      {!hidePaymentUi && !tamarra ? (
        <span className={labelClass}>{t('transferts.reservations.payment.debtorDeposit')}</span>
      ) : null}
      <div className={tamarra ? undefined : styles.autoPaymentEmbed}>
        <AutomaticPaymentMethods
          client={paymentClient}
          totalAmount={total}
          bookingDate={bookingDate}
          partnerIdOverride={partnerId}
          value={valueForPicker}
          onChange={handlePaymentChange}
          disabled={false}
          lockPaymentAmount={lockPaymentAmount}
          lockPaymentAmountMessage={deductNotice}
          appearance={appearance}
        />
      </div>
    </div>
  )
}
